import { Copy, Play, Save, Trash2 } from "lucide-react";
import { InlineWarning, Panel, StatusBadge } from "@/components/common/Primitives";
import { VehicleSelector } from "@/components/vehicle/VehicleSelector";
import { dateTime } from "@/lib/format";

export const DEFAULT_FILTERS = {
  make: "Porsche",
  model: "911",
  make_id: 57,
  model_id: 1950,
  generation: "",
  variant: "",
  year_from: 2019,
  year_to: new Date().getFullYear(),
  mileage_min: "",
  mileage_max: 80000,
  price_min: "",
  price_max: "",
  fuel: "",
  transmission: "",
  body_type: "",
  power_min: "",
  power_max: "",
  colour: "",
  vat_status: "",
  seller_type: "both",
  sources: ["autoscout24_de"],
  min_contribution: 10000,
  min_roi_pct: 8,
  max_bpm: "",
  min_deal_score: 65,
  min_confidence_pct: 60,
  only_opportunities: false,
};

const inputCls =
  "h-8 w-full rounded-sm border border-slate-300 bg-white px-2 text-[12.5px] outline-none transition-colors duration-150 focus:border-blue-500 focus:ring-1 focus:ring-blue-200";

const Field = ({ label, children }) => (
  <div>
    <label className="label-xs">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export const ScanProgressPanel = ({ scan, providerIssues, onViewResults }) => (
  <Panel title="Scanvoortgang" subtitle={`Gestart ${dateTime(scan.created_at)}`} className="mb-3" testid="scan-progress-panel">
    <div className="p-3">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-slate-800" data-testid="scan-stage-message">
          {scan.stage_message}
        </span>
        <span className="mono text-[12px] text-slate-600">{scan.progress}%</span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-sm bg-slate-200">
        <div className="h-full bg-blue-600 transition-[width] duration-500" style={{ width: `${scan.progress}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-600">
        <span>Voertuigen gevonden: <b className="mono">{scan.vehicles_found ?? 0}</b></span>
        <span>Handelskansen: <b className="mono">{scan.opportunities ?? 0}</b></span>
        <span>Hoogste score: <b className="mono">{scan.top_score ?? "—"}</b></span>
      </div>

      {providerIssues.length > 0 && (
        <div className="mt-3 space-y-2">
          {providerIssues.map((p, i) => (
            <InlineWarning
              key={`${p.provider}-${p.message}`}
              tone={p.status === "NOT_CONFIGURED" ? "danger" : "warning"}
              testid={`scan-provider-issue-${i}`}
            >
              <b>{p.label || p.provider}</b> — {p.message}
              {(p.required_credentials || []).length > 0 && (
                <ul className="mt-1 list-inside list-disc text-[11px]">
                  {p.required_credentials.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              )}
            </InlineWarning>
          ))}
        </div>
      )}

      {scan.status === "COMPLETED" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onViewResults}
            data-testid="scan-view-results"
            className="rounded-sm bg-slate-900 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-slate-700"
          >
            Resultaten bekijken ({scan.vehicles_found ?? 0})
          </button>
          {(scan.vehicles_found ?? 0) === 0 && (
            <span className="text-[12px] text-slate-600">
              Geen voertuigen opgehaald — er wordt geen fictieve data gegenereerd.
            </span>
          )}
        </div>
      )}
    </div>
  </Panel>
);

export const VehicleFiltersPanel = ({ filters, set, setFilters, reference }) => (
  <Panel title="Voertuigselectie" testid="vehicle-selection-panel">
    <div className="space-y-2.5 p-3">
      <VehicleSelector selection={filters} onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))} />
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        <Field label="Bouwjaar vanaf">
          <input type="number" value={filters.year_from} data-testid="filter-year-from" onChange={(e) => set("year_from", Number(e.target.value) || "")} className={inputCls} />
        </Field>
        <Field label="Bouwjaar tot">
          <input type="number" value={filters.year_to} data-testid="filter-year-to" onChange={(e) => set("year_to", Number(e.target.value) || "")} className={inputCls} />
        </Field>
        <Field label="Kilometerstand minimaal">
          <input type="number" value={filters.mileage_min} data-testid="filter-mileage-min" onChange={(e) => set("mileage_min", Number(e.target.value) || "")} className={inputCls} />
        </Field>
        <Field label="Kilometerstand maximaal">
          <input type="number" value={filters.mileage_max} data-testid="filter-mileage-max" onChange={(e) => set("mileage_max", Number(e.target.value) || "")} className={inputCls} />
        </Field>
        <Field label="Prijs minimum">
          <input type="number" value={filters.price_min} data-testid="filter-price-min" onChange={(e) => set("price_min", Number(e.target.value) || "")} className={inputCls} />
        </Field>
        <Field label="Prijs maximum">
          <input type="number" value={filters.price_max} data-testid="filter-price-max" onChange={(e) => set("price_max", Number(e.target.value) || "")} className={inputCls} />
        </Field>
        <Field label="Brandstof">
          <select value={filters.fuel} data-testid="filter-fuel" onChange={(e) => set("fuel", e.target.value)} className={inputCls}>
            <option value="">Alle</option>
            {(reference?.fuels || []).map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </Field>
        <Field label="Transmissie">
          <select value={filters.transmission} data-testid="filter-transmission" onChange={(e) => set("transmission", e.target.value)} className={inputCls}>
            <option value="">Alle</option>
            {(reference?.transmissions || []).map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </Field>
        <Field label="Carrosserie">
          <select value={filters.body_type} data-testid="filter-body-type" onChange={(e) => set("body_type", e.target.value)} className={inputCls}>
            <option value="">Alle</option>
            {(reference?.body_types || []).map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </Field>
        <Field label="Vermogen (pk) van / tot">
          <div className="flex gap-1.5">
            <input type="number" value={filters.power_min} data-testid="filter-power-min" onChange={(e) => set("power_min", Number(e.target.value) || "")} className={inputCls} />
            <input type="number" value={filters.power_max} data-testid="filter-power-max" onChange={(e) => set("power_max", Number(e.target.value) || "")} className={inputCls} />
          </div>
        </Field>
        <Field label="Kleur">
          <input value={filters.colour} data-testid="filter-colour" onChange={(e) => set("colour", e.target.value)} className={inputCls} />
        </Field>
        <Field label="BTW-status">
          <select value={filters.vat_status} data-testid="filter-vat-status" onChange={(e) => set("vat_status", e.target.value)} className={inputCls}>
            <option value="">Alle fiscale statussen</option>
            {(reference?.vat_statuses || []).map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </Field>
        <Field label="Verkoper">
          <select value={filters.seller_type} data-testid="filter-seller-type" onChange={(e) => set("seller_type", e.target.value)} className={inputCls}>
            <option value="both">Dealer én particulier</option>
            <option value="Dealer">Dealer</option>
            <option value="Particulier">Particulier</option>
          </select>
        </Field>
      </div>
    </div>
  </Panel>
);

export const SourceMarketPanel = ({ sources, manual, filters, set }) => {
  const toggle = (key, checked) =>
    set("sources", checked ? [...filters.sources, key] : filters.sources.filter((k) => k !== key));
  return (
    <Panel title="Bronmarkt" subtitle="Alleen geautoriseerde live koppelingen leveren voertuigen" testid="source-market-panel">
      <div className="grid gap-2 p-3 md:grid-cols-2">
        {sources.map((s) => (
          <label
            key={s.key}
            className={`flex items-start gap-2 rounded-sm border p-2.5 ${
              s.enabled ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-50 opacity-70"
            }`}
            data-testid={`source-${s.key}`}
          >
            <input
              type="checkbox"
              disabled={!s.enabled}
              checked={filters.sources.includes(s.key)}
              onChange={(e) => toggle(s.key, e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 accent-blue-600"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[12.5px] font-semibold text-slate-800">{s.label}</span>
                {!s.enabled && <StatusBadge status="LATER_BESCHIKBAAR" label="Later beschikbaar" />}
              </div>
              <div className="mt-0.5 text-[11px] leading-tight text-slate-500">{s.description}</div>
            </div>
          </label>
        ))}
        {manual && (
          <label className="flex items-start gap-2 rounded-sm border border-blue-300 bg-blue-50/50 p-2.5" data-testid="source-manual_ingest">
            <input
              type="checkbox"
              checked={filters.sources.includes("manual_ingest")}
              onChange={(e) => toggle("manual_ingest", e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 accent-blue-600"
            />
            <div>
              <div className="text-[12.5px] font-semibold text-slate-800">{manual.label}</div>
              <div className="mt-0.5 text-[11px] leading-tight text-slate-600">{manual.description}</div>
            </div>
          </label>
        )}
      </div>
    </Panel>
  );
};

export const TradingFiltersPanel = ({ filters, set }) => (
  <Panel title="Handelsfilters" testid="trading-filters-panel">
    <div className="space-y-2.5 p-3">
      <Field label="Minimale handelsbijdrage (€)">
        <input type="number" value={filters.min_contribution} data-testid="filter-min-contribution" onChange={(e) => set("min_contribution", Number(e.target.value))} className={inputCls} />
      </Field>
      <Field label="Minimale ROI (%)">
        <input type="number" step="0.1" value={filters.min_roi_pct} data-testid="filter-min-roi" onChange={(e) => set("min_roi_pct", Number(e.target.value))} className={inputCls} />
      </Field>
      <Field label="Maximale geschatte BPM (€) — optioneel">
        <input type="number" value={filters.max_bpm} data-testid="filter-max-bpm" onChange={(e) => set("max_bpm", Number(e.target.value) || "")} className={inputCls} />
      </Field>
      <Field label="Minimale Deal Score">
        <input type="number" value={filters.min_deal_score} data-testid="filter-min-score" onChange={(e) => set("min_deal_score", Number(e.target.value))} className={inputCls} />
      </Field>
      <Field label="Minimale waarderingsbetrouwbaarheid (%)">
        <input type="number" value={filters.min_confidence_pct} data-testid="filter-min-confidence" onChange={(e) => set("min_confidence_pct", Number(e.target.value))} className={inputCls} />
      </Field>
      <label className="flex items-center gap-2 pt-1 text-[12.5px] text-slate-700">
        <input
          type="checkbox"
          checked={filters.only_opportunities}
          data-testid="filter-only-opportunities"
          onChange={(e) => set("only_opportunities", e.target.checked)}
          className="h-3.5 w-3.5 accent-blue-600"
        />
        Alleen interessante handelskansen
      </label>
    </div>
  </Panel>
);

export const SearchProfilesPanel = ({
  profiles,
  profileName,
  setProfileName,
  onSave,
  onApply,
  onRun,
  onDuplicate,
  onDelete,
}) => (
  <Panel title="Zoekprofielen" testid="search-profiles-panel">
    <div className="space-y-2 p-3">
      <div className="flex gap-1.5">
        <input
          value={profileName}
          placeholder="bijv. 992 Duitsland"
          data-testid="profile-name-input"
          onChange={(e) => setProfileName(e.target.value)}
          className={inputCls}
        />
        <button
          type="button"
          onClick={onSave}
          data-testid="save-profile-button"
          className="flex shrink-0 items-center gap-1 rounded-sm border border-slate-300 px-2 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-100"
        >
          <Save className="h-3.5 w-3.5" /> Opslaan
        </button>
      </div>
      <div className="divide-y divide-slate-100">
        {profiles.length === 0 && (
          <p className="py-2 text-[11.5px] text-slate-500">Nog geen zoekprofielen opgeslagen.</p>
        )}
        {profiles.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 py-1.5" data-testid={`profile-${p.id}`}>
            <button type="button" onClick={() => onApply(p)} className="min-w-0 flex-1 text-left">
              <div className="truncate text-[12.5px] font-semibold text-slate-800">{p.name}</div>
              <div className="truncate text-[10.5px] text-slate-500">
                {[p.filters?.make, p.filters?.model, p.filters?.generation].filter(Boolean).join(" ")}
              </div>
            </button>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                title="Scan uitvoeren"
                data-testid={`profile-run-${p.id}`}
                onClick={() => onRun(p)}
                className="rounded-sm border border-slate-300 p-1 text-slate-600 hover:bg-slate-100"
              >
                <Play className="h-3 w-3" />
              </button>
              <button
                type="button"
                title="Dupliceren"
                onClick={() => onDuplicate(p.id)}
                className="rounded-sm border border-slate-300 p-1 text-slate-600 hover:bg-slate-100"
              >
                <Copy className="h-3 w-3" />
              </button>
              <button
                type="button"
                title="Verwijderen"
                data-testid={`profile-delete-${p.id}`}
                onClick={() => onDelete(p.id)}
                className="rounded-sm border border-slate-300 p-1 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Panel>
);
